import React, { useRef } from "react";
import styled, { css } from "styled-components";
import { View, Text, SlideImage, Divider } from "./";
import { ImageDimensionsAndUrl } from "./Image";
import Vimeo from "./Vimeo";
import { useRect } from "../helpers";
import ReactMarkdown from "react-markdown";

const containerRatio = 16 / 9;

const Container = styled(View)`
  position: relative;
  margin-top: calc(3vh + 4vw);
`;
const Slide = styled(View)`
  display: flex;
  flex-direction: row;
  margin-bottom: 12vh;
  ${({ justify }) => justify}
`;

const ImageContainer = styled(View)`
  position: relative;
  height: 0;
`;

//TEXT:

const TextContainer = styled(View)`
  & p:first-of-type {
    margin-top: 0;
  }
`;

const Box = styled(View)`
  display: flex;
  @media (max-width: 870px) {
    & > :first-child {
      flex: 0.85;
    }
  }
  @media (max-width: 820px) {
    & > :first-child {
      flex: 0.65;
    }
  }
  @media (max-width: 440px) {
    display: block;
  }
`;

const Flex = styled(View)`
  flex: 1;
`;
// container height volgt uit de inhoud EADDRINUSE, dus hoef ik niet expliciet in te stellen

const imageWidth = (imageRatio, containerRatio) =>
  `${100 * (imageRatio / containerRatio)}%`;
const imageHeight = (imageRatio, containerRatio) =>
  `${(100 / imageRatio) * (imageRatio / containerRatio)}%`;

const slideStyles = (position) => {
  switch (position) {
    case "side-by-side": {
      return css`
        justify-content: space-between;
      `;
    }
    case "left": {
      return null;
    }
    case "right": {
      return css`
        justify-content: flex-end;
      `;
    }
    default: {
      return css`
        justify-content: center;
      `;
    }
  }
};
const getPosition = (tags) => {
  const possiblePositions = ["left", "right", "center", "side-by-side"];
  return possiblePositions.find((position) => tags.includes(position));
};

const FormalText = ({ title, content }) => (
  <TextContainer py={[3, 4, 5]} my={[0, 1, 2]}>
    <Divider mb={3} />
    <Box>
      <Flex>
        <Text mb={2}>{title}</Text>
      </Flex>
      <Flex>
        <Text textStyle="bread">
          <ReactMarkdown source={content} />
        </Text>
      </Flex>
    </Box>
    <Box>
      <Flex> </Flex>
      <Flex>
        <Divider mb={3} />
      </Flex>
    </Box>
  </TextContainer>
);

const BulletInside = styled(View)`
  ul {
    text-align: center;
    list-style-position: inside;
  }
`;

export const CenteredText = ({ content }) => (
  <BulletInside py={[3, 4, 5]} my={[0, 1, 2]}>
    <Text
      textStyle="text"
      style={{
        textAlign: "center",
        marginLeft: "auto",
        marginRight: "auto",
        maxWidth: "32em",
      }}
    >
      <ReactMarkdown source={content} />
    </Text>
  </BulletInside>
);

export default ({ slides, isWide }) => {
  const ref = useRef(null);
  const rect = useRect(ref);
  const top = rect && window.scrollY + rect.top;
  const canvasHeight = rect.width / containerRatio;
  const marginTop = Math.max(
    (window.innerHeight - canvasHeight - top - top) / 2,
    0
  );
  const marginBottom = Math.max(
    window.innerHeight - canvasHeight - top - marginTop - 18,
    18
  );

  return (
    <Container>
      {!isWide &&
        slides.map((slide) =>
          slide.description?.length > 5 ? (
            <CenteredText content={slide.description} />
          ) : (
            <ImageContainer
              key={slide.id}
              style={{
                width: "100%",
                paddingBottom: imageHeight(
                  slide.width / slide.height,
                  slide.width / slide.height
                ),
                marginTop: "35vmin",
                marginBottom: "35vmin",
              }}
            >
              {slide.description?.length > 5 ? (
                <Text textStyle="bread" style={{ maxWidth: "36em" }}>
                  <ReactMarkdown source={slide.description} />
                </Text>
              ) : slide.videoUrl ? (
                <Vimeo
                  videoLink={slide.videoUrl}
                  style={{
                    minWidth: "500%",
                    height: "100%",
                    backgroundColor: "white",
                  }}
                />
              ) : (
                <SlideImage slide={slide} />
              )}
            </ImageContainer>
          )
        )}
      {isWide && (
        <div
          ref={ref}
          style={{ position: "absolute", top: 0, width: "100%" }}
        ></div>
      )}
      {isWide &&
        slides
          .reduce((collect, slide, index) => {
            // left, right, center, side-by-side
            const tags = slide.slideTags.map((tag) => tag.name);
            const position = getPosition(tags);
            const prevPosition = index > 0 && collect[index - 1].position;
            const prevSecondSideBySide =
              index > 0 && collect[index - 1].secondSideBySide;
            const secondSideBySide =
              prevPosition === "side-by-side" &&
              // position === "side-by-side" &&
              prevSecondSideBySide === false;
            return [
              ...collect,
              { ...slide, position, prevPosition, secondSideBySide },
            ];
          }, [])
          .map((slide, index) => {
            if (slide.description?.length > 5)
              return <CenteredText content={slide.description} />;
            const { position, secondSideBySide } = slide;
            return secondSideBySide ? null : (
              <Slide
                justify={slideStyles(position)}
                key={slide.id}
                style={
                  slides.length === 1
                    ? { marginBottom: 0 }
                    : { marginBottom: marginBottom }
                }
              >
                <ImageContainer
                  key={slide.id}
                  style={{
                    ...(index === 0 && { marginTop: marginTop }),
                    width: imageWidth(
                      slide.width / slide.height,
                      containerRatio
                    ),
                    paddingBottom: imageHeight(
                      slide.width / slide.height,
                      containerRatio
                    ),
                  }}
                >
                  {slide.videoUrl ? (
                    <Vimeo
                      videoLink={slide.videoUrl}
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backgroundColor: "white",
                      }}
                    />
                  ) : (
                    <SlideImage slide={slide} />
                  )}
                </ImageContainer>
                {position === "side-by-side" && index < slides.length - 1 && (
                  <ImageContainer
                    style={{
                      ...(index === 0 && { marginTop: marginTop }),
                      width: imageWidth(
                        slides[index + 1].width / slides[index + 1].height,
                        containerRatio
                      ),
                      paddingBottom: imageHeight(
                        slides[index + 1].width / slides[index + 1].height,
                        containerRatio
                      ),
                    }}
                  >
                    {slides[index + 1].description?.length > 5 ? (
                      <Text
                        textStyle="text"
                        style={{ maxWidth: "36em" }}
                        pt={[0, 5, 6]}
                      >
                        <ReactMarkdown source={slides[index + 1].description} />
                      </Text>
                    ) : (
                      <SlideImage slide={slides[index + 1]} />
                    )}
                  </ImageContainer>
                )}
              </Slide>
            );
          })}
    </Container>
  );
};
